import { AxiosInstance } from '../../../utilities/requestClient';
import { appendPageNameQueryParam } from '../../../utilities/urlBuilder';
import { type EsgPageId } from './esgPages';
import type {
  EsgCodeDescriptions,
  EsgOverviewPageModel,
  EsgPortfolioSummaryPageModel,
  EsgStressTestPageModel,
  IndicatorCompliancePageModel,
  FundEsgTimeSeriesApiResponse,
  IndicatorCoverageRatioPageModel,
} from './types';

export { appendPageNameQueryParam } from '../../../utilities/urlBuilder';
export { EsgPages, type EsgPageId, type EsgPageKey } from './esgPages';

export const ESG_STRESS_TEST_PATH = 'manco-esg-stress-test';
export const PORTFOLIO_SUMMARY_PATH = 'summary-data';
export const ESG_CODE_DESCRIPTIONS_PATH = 'get_esg_code_descriptions';
export const ESG_OVERVIEW_MONITOR_PATH = 'esg-monitor';
export const INDICATOR_COVERAGE_RATIO_PATH = 'indicator-coverage-ratio-monitor';
export const FUND_ESG_TIMESERIES_PATH = 'get_fund_esg_timeseries';
export const INDICATOR_COMPLIANCE_PATH = 'indicator-compliance';

export const getEsgStressTestURL = (fundName: string, positionDate: string) =>
  `${ESG_STRESS_TEST_PATH}/${fundName}/${positionDate}`;

/** GET /manco-esg-stress-test/<fund_name>/<position_date> — fund page envelope. */
export const getEsgStressTest = async (
  fundName: string,
  positionDate: string,
): Promise<EsgStressTestPageModel> => {
  const { data } = await AxiosInstance.get<EsgStressTestPageModel>(
    `/${getEsgStressTestURL(fundName, positionDate)}`,
  );
  return data;
};

export const getEsgFundDataURL = (
  fundName: string,
  positionDate: string,
  pageName: EsgPageId,
) =>
  appendPageNameQueryParam(
    `${PORTFOLIO_SUMMARY_PATH}/${fundName}/${positionDate}`,
    pageName,
  );

/** GET /summary-data/<fund_name>/<position_date>?page_name=<id> — ESG fund page (`FundOverviewPage` envelope). */
export const getEsgFundData = async (
  fundName: string,
  positionDate: string,
  pageName: EsgPageId,
): Promise<EsgPortfolioSummaryPageModel> => {
  const { data } = await AxiosInstance.get<EsgPortfolioSummaryPageModel>(
    `/${getEsgFundDataURL(fundName, positionDate, pageName)}`,
  );
  return data;
};

/** GET /get_esg_code_descriptions — ESG rule/code metadata. */
export const getEsgCodeDescriptions =
  async (): Promise<EsgCodeDescriptions> => {
    const { data } = await AxiosInstance.get<EsgCodeDescriptions>(
      `/${ESG_CODE_DESCRIPTIONS_PATH}`,
    );
    return data;
  };

export const getFundEsgTimeSeriesURL = (fundId: string, positionDate: string) =>
  `${FUND_ESG_TIMESERIES_PATH}/${fundId}/${positionDate}`;

/** GET /get_fund_esg_timeseries/<fund_id>/<position_date> — ESG score/carbon timeseries. */
export const getFundEsgTimeSeriesData = async (
  fundId: string,
  positionDate: string,
): Promise<FundEsgTimeSeriesApiResponse> => {
  const { data } = await AxiosInstance.get<FundEsgTimeSeriesApiResponse>(
    `/${getFundEsgTimeSeriesURL(fundId, positionDate)}`,
  );
  return data;
};

export const getEsgOverviewMonitorURL = (
  positionDate?: string,
  raptorPlatformId?: string,
) => {
  const positionDatePath = positionDate ? `/${positionDate}` : '';
  const queryString = raptorPlatformId
    ? `?raptor_platform_id=${raptorPlatformId}`
    : '';

  return `${ESG_OVERVIEW_MONITOR_PATH}${positionDatePath}${queryString}`;
};

/** GET /esg-monitor — ESG overview (`OverviewPage` envelope). */
export const getEsgOverviewMonitor = async (
  positionDate?: string,
  raptorPlatformId?: string,
): Promise<EsgOverviewPageModel> => {
  const { data } = await AxiosInstance.get<EsgOverviewPageModel>(
    `/${getEsgOverviewMonitorURL(positionDate, raptorPlatformId)}`,
  );
  return data;
};

export const getIndicatorCoverageRatioURL = (
  fundId: string,
  positionDate: string,
) => `${INDICATOR_COVERAGE_RATIO_PATH}/${fundId}/${positionDate}`;

/** GET /indicator-coverage-ratio-monitor/<fund_id>/<position_date> — fund page envelope. */
export const getIndicatorCoverageRatio = async (
  fundId: string,
  positionDate: string,
): Promise<IndicatorCoverageRatioPageModel> => {
  const { data } = await AxiosInstance.get<IndicatorCoverageRatioPageModel>(
    `/${getIndicatorCoverageRatioURL(fundId, positionDate)}`,
  );
  return data;
};

export const getIndicatorComplianceURL = (
  fundId: string,
  positionDate: string,
) => `${INDICATOR_COMPLIANCE_PATH}/${fundId}/${positionDate}`;

/** GET /indicator-compliance/<fund_id>/<position_date> — fund page envelope. */
export const getIndicatorCompliance = async (
  fundId: string,
  positionDate: string,
): Promise<IndicatorCompliancePageModel> => {
  const { data } = await AxiosInstance.get<IndicatorCompliancePageModel>(
    `/${getIndicatorComplianceURL(fundId, positionDate)}`,
  );
  return data;
};
